<ng-container *transloco="let t">
  <p-confirmdialog />

  <!-- Use flex with column direction and set a fixed height -->
  <div class="flex flex-col h-full min-h-[50vh] max-h-[80vh]">
    <!-- Content section - scrollable -->
    <div class="flex-1 overflow-y-auto">
      <div class="mb-5">
        <p class="dark:text-surface-400">{{ t("app.organization-settings.editAutomationUserDialog.description") }}</p>
      </div>
      <p-fluid class="flex flex-col gap-2">
        <!-- ID, only in Edit mode -->
        @if (dialogMode === EditAutomationDialogModeEnum.Edit) {
          <div class="flex flex-col gap-2">
            <label class="font-semibold" for="automationUserId">{{
              t("app.organization-settings.editAutomationUserDialog.id")
            }}</label>
            <span id="automationUserId">{{ automationUser?.id }}</span>
          </div>
        }
        <form [formGroup]="form" class="flex flex-col gap-2">
          <!-- Name -->
          <div class="flex flex-row gap-1">
            <label class="font-semibold" for="automationUserName">{{
              t("app.organization-settings.editAutomationUserDialog.name")
            }}</label>
            <span class="text-red-500">*</span>
          </div>
          <input type="text" id="automationUserName" pInputText formControlName="name" />
          <!-- Is-read-only checkbox. For external users, only visible in Edit mode. -->
          @if (
            dialogMode === EditAutomationDialogModeEnum.Edit ||
            (dialogMode === EditAutomationDialogModeEnum.Create && openingUser.isInternal)
          ) {
            <div class="flex flex-row gap-2">
              <p-checkbox
                formControlName="isReadOnlyForExternals"
                [binary]="true"
                inputId="isReadOnlyForExternalsCheckbox"
              />
              <label for="isReadOnlyForExternalsCheckbox" class="select-none">{{
                t("app.organization-settings.editAutomationUserDialog.isReadOnlyForExternals")
              }}</label>
            </div>
          }
          <!-- Access Role dropdown -->
          <div class="flex flex-col gap-2">
            <div class="flex flex-row gap-1">
              <label class="font-semibold" for="accessRoleId">{{
                t("app.organization-settings.editAutomationUserDialog.accessRole")
              }}</label>
              <span class="text-red-500">*</span>
            </div>
            <p-select
              id="accessRoleId"
              formControlName="accessRoleId"
              [options]="accessRoles"
              optionLabel="name"
              optionValue="id"
              appendTo="body"
              [placeholder]="t('app.organization-settings.editAutomationUserDialog.accessRolePlaceholder')"
            />
          </div>
        </form>
        <!-- API key display box -->
        @if (dialogMode === EditAutomationDialogModeEnum.Edit) {
          <div class="flex flex-col gap-2">
            <label class="font-semibold" for="apiKey">{{
              t("app.organization-settings.editAutomationUserDialog.apiKey")
            }}</label>
            <div class="flex flex-row gap-1">
              <p-iconfield iconPosition="right" class="flex-1">
                <input
                  type="text"
                  id="apiKey"
                  class="bg-gray-100 dark:bg-gray-800"
                  pInputText
                  readonly
                  [ngModel]="apiKey ?? '************'"
                />

                @if (apiKey) {
                  <!-- force-override p-inputicon's default positioning, because buttons are differently-sized -->
                  <p-inputicon class="!mt-0 -translate-y-1/2">
                    <!-- Copy API key button -->
                    <p-button
                      icon="pi pi-copy"
                      [text]="true"
                      [rounded]="true"
                      (onClick)="copyApiKeyToClipboard()"
                      [pTooltip]="t('app.organization-settings.editAutomationUserDialog.copyApiKeyButtonTooltip')"
                    />
                  </p-inputicon>
                }
              </p-iconfield>

              @if (dialogMode === EditAutomationDialogModeEnum.Edit && shouldAllowEditing()) {
                <!-- Rotate API key button -->
                <p-button
                  class="flex"
                  icon="pi pi-refresh"
                  (onClick)="openRotateApiKeyConfirmationDialog()"
                  [pTooltip]="t('app.organization-settings.editAutomationUserDialog.rotateApiKeyButtonTooltip')"
                />
              }
            </div>
            @if (apiKey) {
              <span class="text-red-500">{{
                t("app.organization-settings.editAutomationUserDialog.apiKeyCopyWarning")
              }}</span>
            }
          </div>
        }
      </p-fluid>
    </div>

    <!-- Footer section, fixed -->
    <div class="mt-3 pt-3 border-t border-surface-200 dark:border-surface-700">
      <div class="flex flex-row justify-between flex-wrap gap-3 w-full">
        <!-- Left-aligned: Delete button -->
        <div>
          @if (shouldAllowDeletion() && this.dialogMode === EditAutomationDialogModeEnum.Edit) {
            <p-button
              [label]="t('app.common.delete')"
              icon="pi pi-trash"
              variant="outlined"
              severity="danger"
              (click)="openConfirmationDeleteDialog()"
              [disabled]="form.invalid || loading"
              [loading]="loading"
            />
          }
        </div>

        <!-- Right-aligned: Cancel and Save buttons -->
        <div class="flex gap-3 justify-end">
          <p-button
            [label]="
              dialogMode === EditAutomationDialogModeEnum.Create ? t('app.common.cancel') : t('app.common.close')
            "
            severity="secondary"
            (click)="ref.close()"
            [disabled]="loading"
          ></p-button>
          @if (shouldAllowEditing()) {
            <p-button
              [label]="t('app.common.save')"
              (click)="submitEditForm()"
              [disabled]="!form.dirty || form.invalid || loading"
              [loading]="loading"
            ></p-button>
          }
        </div>
      </div>
    </div>
  </div>
</ng-container>
